import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Upload, Loader2, FileSpreadsheet, RotateCcw, Pencil } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Empty, ConfirmDialog } from "@/components/admin/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { CodeHistoryDialog } from "@/components/admin/CodeHistory";
import { EditExpiration, BulkExpiration } from "@/components/admin/CodeExpiration";
import { IssueCodes } from "@/components/admin/IssueCodes";

export function CodesTable({ codes, onChanged, hideOrg }: { codes: any[]; onChanged: () => void; hideOrg?: boolean }) {
  const { can, user } = useAuth();
  const isAdmin = !!user && user.role === "admin";
  const [voiding, setVoiding] = useState<any>(null);
  const [resetting, setResetting] = useState<any>(null);
  const [expEdit, setExpEdit] = useState<any>(null);
  const [history, setHistory] = useState<string | null>(null);
  if (!codes.length) return <Empty text="No codes found." />;
  return (
    <div className="max-h-[65vh] overflow-auto overscroll-contain" data-testid="codes-scroll-box">
      <table className="tbl" data-testid="codes-table">
        <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_#E2E8F0]"><tr><th>Code</th>{!hideOrg && <th>Organization</th>}<th>Status</th><th>Source</th><th>Issued</th><th>Expires</th><th>Used</th><th /></tr></thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.id} data-testid={`code-row-${c.code}`}>
              <td><button className="font-mono text-[13px] font-semibold tracking-wider text-[#1B365D] hover:underline" title="View code history" onClick={() => setHistory(c.code)} data-testid={`code-history-open-${c.code}`}>{c.code}</button></td>
              {!hideOrg && <td><Link to={`/admin/organizations/${c.org_id}`} className="text-[#1B365D] hover:underline">{c.org_name}</Link></td>}
              <td><StatusBadge status={c.status} testId={`code-status-${c.code}`} /></td>
              <td className="text-xs capitalize text-[#64748B]">{c.source}{c.expired_reason === "incomplete_24h" && " · 24h lockout"}</td>
              <td className="text-xs">{fmtDate(c.issued_at)}</td>
              <td className="whitespace-nowrap text-xs">{fmtDate(c.expires_at)}
                {can("manage_codes") && (["unused", "reserved"].includes(c.status) || (c.status === "expired" && c.expired_reason === "past_expiry")) && (
                  <button className="ml-1 rounded p-1 text-[#64748B] hover:bg-[#F1EFEA] hover:text-[#1B365D]" title="Change expiration" onClick={() => setExpEdit(c)} data-testid={`code-expiration-edit-${c.code}`}><Pencil className="h-3.5 w-3.5" /></button>
                )}
              </td>
              <td className="text-xs">{c.application_id ? <Link to={`/admin/cases/${c.application_id}`} className="text-[#1B365D] hover:underline">{fmtDate(c.used_at || c.reserved_at)}</Link> : c.used_at ? <span data-testid={`code-used-by-${c.code}`}>{fmtDate(c.used_at)}{c.used_by_name ? ` · ${c.used_by_name}` : ""}</span> : "—"}</td>
              <td className="space-x-2 whitespace-nowrap text-right">
                {(can("manage_codes") && (c.status === "reserved" || (c.status === "expired" && c.expired_reason === "incomplete_24h")) || (isAdmin && c.status === "used")) && (
                  <button className="btn-secondary btn-sm" data-testid={`code-reset-${c.code}`} onClick={() => setResetting(c)}><RotateCcw className="h-4 w-4" /> Reset to unused</button>
                )}
                {can("manage_codes") && ["unused", "reserved"].includes(c.status) && (
                  <button className="btn-secondary btn-sm text-[#B91C1C]" data-testid={`code-void-${c.code}`} onClick={() => setVoiding(c)}><Ban className="h-4 w-4" /> Void</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <CodeHistoryDialog code={history} onClose={() => setHistory(null)} />
      <EditExpiration code={expEdit} onClose={() => setExpEdit(null)} onDone={onChanged} />
      <ConfirmDialog
        open={!!resetting}
        onOpenChange={(o) => !o && setResetting(null)}
        testId="code-reset-dialog"
        title={`Reset code ${resetting?.code} to unused?`}
        description={resetting?.status === "used"
          ? "This code was used for a submitted application. Resetting it makes the code work like new and CANCELS that application: it is marked Cancelled and left off the monthly bill. Please type the reason below (required)."
          : "This clears the block so the code works like new. Any unfinished application started with it is closed, and the applicant will start over from the beginning."}
        confirmLabel="Reset to unused"
        withNote
        onConfirm={async (note) => {
          try {
            await api.post(`/admin/codes/${resetting.id}/reset`, { note });
            toast.success(`Code ${resetting.code} is unused again`);
            onChanged();
          } catch (e) {
            toast.error(errMsg(e));
            throw e;
          }
        }}
      />
      <ConfirmDialog
        open={!!voiding}
        onOpenChange={(o) => !o && setVoiding(null)}
        testId="code-void-dialog"
        title={`Void code ${voiding?.code}?`}
        description="A voided code can never be used again. Any application in progress with this code will be locked."
        confirmLabel="Void code"
        danger
        withNote
        onConfirm={async (note) => {
          try {
            await api.post(`/admin/codes/${voiding.id}/void`, { note });
            toast.success("Code voided");
            onChanged();
          } catch (e) {
            toast.error(errMsg(e));
            throw e;
          }
        }}
      />
    </div>
  );
}

function LegacyImport({ onDone }: { onDone: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const upload = async (file: File) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/codes/import", fd);
      setResult(data);
      toast.success(`Imported ${data.imported} legacy codes`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <Card title="Legacy code import" testId="legacy-import-card">
      <div className="p-6">
        <p className="text-[15px] text-[#475569]">Upload a CSV or Excel (.xlsx) file exported from the old system. Required columns: <code className="rounded bg-[#F1EFEA] px-1.5">code</code>, <code className="rounded bg-[#F1EFEA] px-1.5">organization</code>. Optional: <code className="rounded bg-[#F1EFEA] px-1.5">organization_email</code>, <code className="rounded bg-[#F1EFEA] px-1.5">expires_at</code> (YYYY-MM-DD), <code className="rounded bg-[#F1EFEA] px-1.5">issued</code>, <code className="rounded bg-[#F1EFEA] px-1.5">date used</code>, <code className="rounded bg-[#F1EFEA] px-1.5">usedby</code>. Rows with a date used or applicant name are imported as used and can never be redeemed. Codes with an issue date are good for 2 years from that date. Re-uploading a file corrects the dates on legacy codes already imported; other duplicates are skipped.</p>
        <input ref={ref} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="legacy-import-file-input" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        <button className="btn-secondary btn-sm mt-4" disabled={busy} onClick={() => ref.current?.click()} data-testid="legacy-import-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choose CSV or Excel file
        </button>
        {result && (
          <div className="mt-4 rounded-xl bg-[#F1EFEA] p-4 text-sm" data-testid="legacy-import-result">
            <p className="flex items-center gap-2 font-semibold"><FileSpreadsheet className="h-4 w-4" /> {result.imported} imported{result.imported_used ? ` (${result.imported_used} as used)` : ""} · {result.updated_dates ?? 0} dates corrected · {result.skipped_duplicates} duplicates skipped · {result.errors.length} errors</p>
            {result.errors.map((e: string) => <p key={e} className="mt-1 text-[#B91C1C]">{e}</p>)}
          </div>
        )}
      </div>
    </Card>
  );
}

const STATUSES = ["", "unused", "reserved", "used", "expired", "voided"];

export default function Codes() {
  const { can } = useAuth();
  const [codes, setCodes] = useState<any[]>([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/admin/codes", { params: { status: status || undefined, q: q || undefined } }).then((r) => setCodes(r.data)), [status, q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  return (
    <div>
      <PageHeader title="Code inventory" subtitle="Every authorization code across all organizations. Click any code to see its full history." />
      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div>
          <div className="mb-4 flex flex-wrap gap-3">
            <input className="field-input max-w-xs" placeholder="Search code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="codes-search-input" />
            <select className="field-input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="codes-status-filter">
              {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
            </select>
          </div>
          <p className="mb-2 text-xs text-[#64748B]" data-testid="codes-count">{codes.length.toLocaleString()} code{codes.length === 1 ? "" : "s"}{codes.length >= 2000 ? " (showing first 2,000, use search or filter to narrow)" : ""}</p>
          <Card><CodesTable codes={codes} onChanged={load} /></Card>
        </div>
        {(can("manage_codes") || can("approve_orders")) && <div className="space-y-6">{can("approve_orders") && <IssueCodes onDone={load} />}{can("manage_codes") && <><BulkExpiration onDone={load} /><LegacyImport onDone={load} /></>}</div>}
      </div>
    </div>
  );
}
